import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import BarChart from 'components/charts/BarChart';

const MetricsChart = ({ siteId, ymetricaId, currentLang }) => {
  const { token, API_URL, getCsrfToken } = useAuth();
  
  const [chartData, setChartData] = useState([]);
  const [chartOptions, setChartOptions] = useState({});
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [metricsError, setMetricsError] = useState(null);

  const fetchMetricsData = async () => {
    if (!ymetricaId || ymetricaId === '0' || String(ymetricaId).trim() === '') {
      return;
    }

    try {
      setLoadingMetrics(true);
      setMetricsError(null);

      // Получаем токен доступа к Яндекс.Метрике через наш бэкенд
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      // Получаем готовый токен доступа к Яндекс.Метрике
      const tokenResponse = await fetch(`${API_URL}${currentLang}/yandex-metrica/token?site_id=${siteId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const tokenData = await tokenResponse.json();
      
      if (!tokenResponse.ok || !tokenData.success) {
        throw new Error(currentLang === 'ru' ? 'Не удалось получить токен доступа к Яндекс.Метрике' : 'Failed to get Yandex Metrica access token');
      }

      const yandexToken = tokenData.token;

      // Вычисляем даты для последних 45 дней
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 44); // 45 дней включая сегодня

      const date1 = startDate.toISOString().split('T')[0];
      const date2 = endDate.toISOString().split('T')[0];

      // Запрос к API Яндекс.Метрики
      const metricsResponse = await fetch(
        `https://api-metrika.yandex.net/stat/v1/data?ids=${ymetricaId}&date1=${date1}&date2=${date2}&metrics=ym:s:visits&dimensions=ym:s:date&sort=ym:s:date`,
        {
          method: 'GET',
          headers: {
            'Authorization': `OAuth ${yandexToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!metricsResponse.ok) {
        throw new Error(currentLang === 'ru' ? 'Ошибка получения данных из Яндекс.Метрики' : 'Error getting data from Yandex Metrica');
      }

      const metricsData = await metricsResponse.json();
      
      if (metricsData.data && metricsData.data.length > 0) {
        // Подготавливаем данные для ApexCharts
        const categories = [];
        const seriesData = [];
        
        metricsData.data.forEach(item => {
          const date = new Date(item.dimensions[0].name);
          categories.push(date.toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US', {
            day: '2-digit',
            month: '2-digit'
          }));
          seriesData.push(parseInt(item.metrics[0]));
        });
        
        // Создаем данные для BarChart
        const newChartData = [{
          name: currentLang === 'ru' ? 'Посетители' : 'Visitors',
          data: seriesData,
          color: "#3B82F6",
        }];

        // Создаем опции для BarChart
        const newChartOptions = {
          legend: {
            show: false,
          },
          theme: {
            mode: "light",
          },
          chart: {
            type: "bar",
            toolbar: {
              show: false,
            },
          },
          dataLabels: {
            enabled: false,
          },
          plotOptions: {
            bar: {
              borderRadius: 4,
              columnWidth: '60%',
              distributed: false,
            },
          },
          tooltip: {
            style: {
              fontSize: "12px",
              fontFamily: undefined,
              backgroundColor: "#000000"
            },
            theme: 'dark',
            y: {
              formatter: function(value) {
                return value + (currentLang === 'ru' ? ' посетителей' : ' visitors');
              }
            }
          },
          grid: {
            show: false,
          },
          xaxis: {
            axisBorder: {
              show: false,
            },
            axisTicks: {
              show: false,
            },
            labels: {
              style: {
                colors: "#A3AED0",
                fontSize: "10px",
                fontWeight: "500",
              },
              rotate: -45,
              rotateAlways: false,
            },
            type: "text",
            categories: categories,
          },
          yaxis: {
            show: false,
          },
          colors: ["#3B82F6"],
          fill: {
            type: "solid",
            opacity: 0.8
          }
        };

        setChartData(newChartData);
        setChartOptions(newChartOptions);
      } else {
        setMetricsError(currentLang === 'ru' ? 'Данные о посещаемости отсутствуют в Яндекс.Метрике' : 'No traffic data available in Yandex Metrica');
      }
    } catch (error) {
      
      setMetricsError(error.message || 'Ошибка сети при получении данных метрики');
    } finally {
      setLoadingMetrics(false);
    }
  };

  useEffect(() => {
    if (siteId && ymetricaId && ymetricaId !== '0' && String(ymetricaId).trim() !== '') {
      fetchMetricsData();
    }
  }, [siteId, ymetricaId, currentLang]);

  if (!ymetricaId || ymetricaId === '0' || String(ymetricaId).trim() === '') {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-bold mb-4">
        {currentLang === 'ru' ? 'Посещаемость за 45 дней (Яндекс.Метрика)' : 'Traffic for 45 days (Yandex Metrica)'}
      </h3>
      
      {loadingMetrics ? (
        <div className="flex justify-center items-center h-64">
          <div className="flex items-center space-x-2">
            <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></span>
            <span className="text-gray-600">
              {currentLang === 'ru' ? 'Загрузка данных...' : 'Loading data...'}
            </span>
          </div>
        </div>
      ) : metricsError ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">{metricsError}</p>
            <button
              onClick={fetchMetricsData}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
            >
              {currentLang === 'ru' ? 'Повторить' : 'Retry'}
            </button>
          </div>
        </div>
      ) : chartData.length > 0 ? (
        <div className="h-80">
          <BarChart
            chartData={chartData}
            chartOptions={chartOptions}
          />
        </div>
      ) : (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-gray-400 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <p className="text-gray-600">
              {currentLang === 'ru' ? 'Данные о посещаемости отсутствуют в Яндекс.Метрике' : 'No traffic data available in Yandex Metrica'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsChart; 